import React, { useState } from 'react';
import { X } from 'lucide-react';
import { LoginPage } from './Auth/LoginPage';
import { RegisterPage } from './Auth/RegisterPage';
import { Logo } from './Logo';
import { LegalModal } from './Legal/LegalModal';

const capabilities = [
  { index: '01', title: 'Instant Deep Analysis', description: 'Submit a ticker and receive a comprehensive research memo in minutes, not hours.' },
  { index: '02', title: 'AI-Powered Research Memos', description: 'Every analysis produces a structured, readable memo covering business model, financial health, competitive position, and risk factors.' },
  { index: '03', title: 'Clear Verdicts', description: 'Each analysis concludes with a clear directional verdict and the reasoning behind it.' },
  { index: '04', title: 'Early Signal Detection', description: 'A dedicated scan identifies companies that may be approaching a significant business inflection — before the broader market catches on.' },
  { index: '05', title: 'Capitol Edge', description: 'Track legislative activity to identify potential winners and losers based on emerging regulatory action.' },
  { index: '06', title: 'PDF Export', description: 'Every memo is exportable as a clean, formatted PDF suitable for personal records or sharing with a financial advisor.' },
  { index: '07', title: 'Built for Serious Investors', description: "Not designed for day traders or casual browsing. Oracle Insight is for investors who want to understand what they own and why it's worth owning." },
];

export const LandingPage: React.FC = () => {
  const [authMode, setAuthMode] = useState<'closed' | 'login' | 'register'>('closed');
  const [legalView, setLegalView] = useState<'terms' | 'privacy' | null>(null);

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      {/* Sticky Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-paper/85 backdrop-blur-md border-b border-ink-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Logo size={34} variant="lockup" />
          <button
            onClick={() => setAuthMode('login')}
            className="text-sm font-medium tracking-wide text-ink border border-ink-300 hover:bg-surface-2 transition-colors px-5 py-2 rounded-md"
          >
            Sign In
          </button>
        </div>
      </nav>

      {/* Hero — two-column: copy left, memo specimen right */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 border-b border-ink-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div>
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-oxblood mb-5">
              Value-Investing Equity Research
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight leading-[1.06] text-ink">
              Institutional-Grade Research. Generated in Minutes.
            </h1>
            <p className="text-lg md:text-xl text-ink-600 mt-6 max-w-xl leading-relaxed">
              AI-powered equity analysis rooted in value investing principles. Built for investors who think in years, not minutes.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <button
                onClick={() => setAuthMode('login')}
                className="inline-flex items-center justify-center bg-oxblood text-paper font-medium tracking-wide px-7 py-3 rounded-md hover:bg-oxblood-800 transition-colors"
              >
                Access Oracle Insight
              </button>
              <button
                onClick={() => setAuthMode('register')}
                className="inline-flex items-center justify-center border border-ink-300 text-ink font-medium tracking-wide px-7 py-3 rounded-md hover:bg-surface-2 transition-colors"
              >
                Request Access
              </button>
            </div>
          </div>

          {/* Illustrative memo specimen */}
          <div className="bg-surface border border-ink-200 rounded-lg p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-ink-500">Investment Memo</span>
                  <span className="text-[10px] font-medium uppercase tracking-wide text-oxblood border border-oxblood-100 rounded px-1.5 py-0.5">Sample</span>
                </div>
                <h3 className="font-serif text-xl font-medium text-ink mt-1">Apple Inc.</h3>
                <div className="text-xs text-ink-600 tabular-nums">AAPL · Nasdaq</div>
              </div>
              <div className="border border-warning-600 rounded-md px-3 py-1.5 text-center shrink-0">
                <div className="text-[9px] font-medium uppercase tracking-[0.14em] text-ink-500">Verdict</div>
                <div className="font-serif text-base font-medium leading-tight text-warning-600">Watch</div>
              </div>
            </div>

            <div className="border-t border-ink-200 my-4" />

            <div className="space-y-2.5 text-sm tabular-nums">
              <div className="flex justify-between"><span className="text-ink-600">Intrinsic value / sh</span><span className="font-medium text-ink">$182</span></div>
              <div className="flex justify-between"><span className="text-ink-600">Current price</span><span className="font-medium text-ink">$214</span></div>
              <div className="flex justify-between"><span className="text-ink-600">Margin of safety</span><span className="font-medium text-danger-600">−15%</span></div>
            </div>

            <div className="mt-5">
              <div className="flex justify-between text-[10px] text-ink-500 tabular-nums mb-1.5">
                <span>Bear $148</span><span>Base $182</span><span>Bull $231</span>
              </div>
              <div className="relative h-1.5 rounded-full bg-surface-2 border border-ink-200">
                <div className="absolute inset-y-0 left-[24%] right-[22%] bg-success-600/30 rounded-full" />
                <div className="absolute left-[78%] top-1/2 -translate-y-1/2 w-0.5 h-3 bg-ink" />
              </div>
            </div>

            <div className="text-[10px] text-ink-500 mt-4">Illustrative sample — not a recommendation.</div>
          </div>
        </div>
      </section>

      {/* What It Does + Why We Built This — editorial label-in-margin */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-surface border-b border-ink-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-14">
          <span className="text-xs font-medium uppercase tracking-[0.18em] text-oxblood md:pt-2">What It Does</span>
          <p className="text-2xl md:text-3xl text-ink font-serif leading-snug max-w-3xl">
            Oracle Insight evaluates public companies using rigorous, fundamental analysis. Simply enter a stock ticker, and the engine produces a deeply researched, structured memo with a clear verdict and supporting rationale.
          </p>

          <span className="text-xs font-medium uppercase tracking-[0.18em] text-oxblood md:pt-2">Why We Built This</span>
          <div className="max-w-3xl space-y-6">
            <p className="text-lg text-ink-600 leading-relaxed font-serif">
              Most retail investors have access to the same raw data as professionals — but not the same analytical frameworks. Earnings reports, SEC filings, financial ratios, and market signals are all publicly available, yet synthesizing them into a coherent investment thesis requires either years of experience or an expensive analyst team.
            </p>
            <p className="text-lg text-ink-600 leading-relaxed font-serif">
              Oracle Insight was built to close that gap. The platform applies a consistent, rigorous analytical process to every company it evaluates — the same questions asked every time, in the same order, with the same standard of evidence required before a verdict is rendered. No hype. No headlines. Just structured analysis.
            </p>
          </div>
        </div>
      </section>

      {/* The Philosophy — editorial label-in-margin */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-b border-ink-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-8">
          <span className="text-xs font-medium uppercase tracking-[0.18em] text-oxblood md:pt-2">The Philosophy</span>
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold tracking-tight text-ink">Built on the shoulders of giants</h2>

            <blockquote className="border-l-2 border-oxblood pl-6 my-10">
              <p className="text-2xl md:text-3xl font-serif italic text-ink leading-snug">
                "Price is what you pay. Value is what you get."
              </p>
              <cite className="block not-italic mt-4 text-xs uppercase tracking-[0.18em] text-oxblood font-medium">— Warren Buffett</cite>
            </blockquote>

            <div className="space-y-6 text-lg text-ink-600 font-serif leading-relaxed">
              <p>
                Oracle Insight is grounded in the principles of value investing as practiced and taught by Warren Buffett and Charlie Munger over decades at Berkshire Hathaway.
              </p>
              <p>
                The core belief: a stock is not a ticker symbol — it is a fractional ownership stake in a real business. The quality of that business, the durability of its competitive advantages, and the integrity of its management matter far more than short-term price movements.
              </p>
              <p>
                Every analysis Oracle Insight produces asks the same fundamental questions that a disciplined value investor would ask before committing capital: Does this business have durable competitive advantages? Is management allocating capital intelligently? Is the financial foundation strong enough to survive adversity? Is there a margin of safety in the current valuation?
              </p>
              <p>
                This is not momentum trading. This is not technical analysis. This is not sentiment chasing. Oracle Insight applies the same patient, evidence-based lens that has produced the greatest long-term investment track record in history — made accessible through AI.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities — numbered, hairline-ruled grid (no icon tiles) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-surface border-b border-ink-200">
        <div className="max-w-6xl mx-auto">
          <span className="block text-xs font-medium uppercase tracking-[0.18em] text-oxblood mb-10">Platform Capabilities</span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10">
            {capabilities.map((c) => (
              <div key={c.index} className="border-t border-ink-200 py-5">
                <div className="flex gap-3">
                  <span className="font-serif text-sm text-oxblood leading-6">{c.index}</span>
                  <div>
                    <h3 className="font-serif text-base font-medium text-ink">{c.title}</h3>
                    <p className="text-sm text-ink-600 leading-relaxed mt-1">{c.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For + Join — editorial label-in-margin */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-b border-ink-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-10 gap-y-8 items-start">
          <span className="text-xs font-medium uppercase tracking-[0.18em] text-oxblood md:pt-2">Who It's For</span>
          <div className="max-w-3xl">
            <p className="text-xl md:text-2xl text-ink-600 font-serif leading-relaxed">
              Designed for self-directed investors, long-term holders, and people who want structured fundamental analysis instead of chasing short-term price action.
            </p>
            <div className="bg-oxblood-50 border border-oxblood-100 rounded-lg p-8 md:p-10 mt-10">
              <h3 className="text-lg font-serif font-medium tracking-wide text-ink mb-3">Join Oracle Insight</h3>
              <p className="text-ink-600 mb-7 leading-relaxed max-w-2xl">
                Oracle Insight is currently in a private testing phase with a select group of investors. You can submit a request for access below — all requests are reviewed manually and not all will be approved.
              </p>
              <button
                onClick={() => setAuthMode('register')}
                className="inline-flex items-center justify-center px-7 py-3 bg-oxblood text-paper font-medium tracking-wide rounded-md hover:bg-oxblood-800 transition-colors"
              >
                Request Access
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer / Legal Disclaimer Block */}
      <footer className="bg-surface-2 text-ink-600 py-16 px-4 md:px-8 border-t border-ink-200">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          <Logo size={30} variant="lockup" />

          <div className="text-[11px] leading-relaxed space-y-4 max-w-5xl text-ink-600">
            <p>
              <strong className="text-ink font-medium">Not Investment Advice / Not a Registered Investment Adviser:</strong> Oracle Insight is an informational and educational tool only. Nothing on this platform, including any analysis, verdict, score, memo, signal, or other output, constitutes investment advice, financial advice, trading advice, or any recommendation to buy, sell, or hold any security. No content produced by Oracle Insight should be construed as a solicitation or offer to buy or sell any financial instrument. Oracle Insight is not registered as an investment adviser with the U.S. Securities and Exchange Commission (SEC) or any state securities authority. Use of this platform does not create an adviser-client relationship of any kind. Oracle Insight does not manage assets, execute trades, or provide personalized investment recommendations.
            </p>
            <p>
              <strong className="text-ink font-medium">Consult a Qualified Professional / AI-Generated Content:</strong> Before making any investment decision, users are strongly encouraged to consult with a qualified financial advisor, certified financial planner (CFP), or licensed investment professional who can evaluate their individual financial situation, goals, risk tolerance, and tax circumstances. Oracle Insight is not a substitute for personalized professional advice. All analysis, memos, verdicts, and signals produced by Oracle Insight are generated by artificial intelligence using publicly available data. AI-generated content may contain errors, omissions, or outdated information. Outputs should not be relied upon as the sole basis for any investment decision.
            </p>
            <p>
              <strong className="text-ink font-medium">Past Performance &amp; Data Accuracy:</strong> Any references to historical data, past market conditions, or prior company performance are provided for informational context only. Past performance is not indicative of future results. No representation is made that any analysis will result in profits or avoid losses. Financial data displayed on this platform is sourced from third-party data providers. Oracle Insight makes no warranty, express or implied, regarding the accuracy, completeness, timeliness, or fitness for any purpose of such data. Users should independently verify any information before relying on it.
            </p>
            <p>
              <strong className="text-ink font-medium">No Liability &amp; Forward-Looking Statements:</strong> To the fullest extent permitted by law, Oracle Insight and its operators shall not be liable for any investment losses, financial damages, or consequential damages of any kind arising from use of this platform or reliance on its outputs. Certain analysis outputs may include projections, estimates, or forward-looking statements. These are inherently uncertain and subject to change. Actual results may differ materially from any projections or estimates contained in Oracle Insight outputs.
            </p>
          </div>

          <div className="pt-8 border-t border-ink-200 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] text-ink-500">
            <p>&copy; {new Date().getFullYear()} Oracle Insight. All rights reserved.</p>
            <div className="flex gap-8">
              <button onClick={() => setLegalView('terms')} className="hover:text-oxblood transition-colors">Terms of Service</button>
              <button onClick={() => setLegalView('privacy')} className="hover:text-oxblood transition-colors">Privacy Policy</button>
            </div>
          </div>
        </div>
      </footer>

      <LegalModal view={legalView} onClose={() => setLegalView(null)} />

      {/* Auth Modal Overlay */}
      {authMode !== 'closed' && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm overflow-y-auto">
          <div className="absolute inset-0" onClick={() => setAuthMode('closed')} />
          <div className="relative z-10 w-full max-w-md my-8">
            <button
              onClick={() => setAuthMode('closed')}
              className="absolute -top-12 right-0 p-2 text-paper/70 hover:text-paper transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            {authMode === 'login' ? (
              <LoginPage onSwitchToRegister={() => setAuthMode('register')} />
            ) : (
              <RegisterPage onSwitchToLogin={() => setAuthMode('login')} onOpenLegal={setLegalView} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
